import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { TableInstance } from 'react-table';
import { quoteStatusOptions } from 'shared/constants';
import { defaultSearchFilters, useQuoteListState } from 'shared/contexts/quotes-summary-context';
import { SelectField } from 'shared/controls';
import { useDebounce } from 'shared/hooks';
import { useSessionStorage } from 'shared/hooks/use-session-storage';
import { QuotesSummaryItem } from 'shared/types';

interface QuoteSummaryStatusFilterProps {
  quotesSummaryTableInstance: TableInstance<QuotesSummaryItem>;
}

export function QuoteSummaryStatusFilter({ quotesSummaryTableInstance }: QuoteSummaryStatusFilterProps) {
  const methods = useForm();
  const { setQuoteTableState, formSearchFilters, setFormSearchFilters } = useQuoteListState();
  const setSearchFiltersSessionStorage = useSessionStorage('quoteSummaryFilters', defaultSearchFilters)[1];
  const { gotoPage } = quotesSummaryTableInstance;

  const handleSearchChanged = useCallback(
    (value: string | string[] | null) => {
      const valueToFilter = typeof value === 'string' ? value : '';
      setFormSearchFilters({
        ...formSearchFilters,
        status: valueToFilter,
      });
    },
    [setFormSearchFilters, formSearchFilters]
  );

  const debouncedSearch = useDebounce(formSearchFilters, 500);
  useEffect(() => {
    setQuoteTableState(prevState => {
      setSearchFiltersSessionStorage(debouncedSearch);
      return {
        ...prevState,
        searchFilters: debouncedSearch,
        tableState: {
          ...prevState.tableState,
          pageIndex: 0,
        },
      };
    });
    gotoPage(0);
  }, [debouncedSearch, setQuoteTableState, gotoPage, setSearchFiltersSessionStorage]);

  return (
    <FormProvider {...methods}>
      <SelectField
        name='status'
        label='Status Filter'
        defaultValue={formSearchFilters.status}
        options={[...quoteStatusOptions]}
        getOptionLabel={opt => opt}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return true;
          return option === value;
        }}
        onSelectChange={handleSearchChanged}
      />
    </FormProvider>
  );
}
